import classNames from 'classnames';
import useCallbackRef from 'hooks/useCallbackRef';
import { Button, Loader } from 'ncoded-component-library';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
} from 'react';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import CheckboxFieldGroup from 'components/CheckboxFieldGroup';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { ManageTableColumns } from 'models/User';
import { Option } from 'models/Global';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';

import './ManageListViewDrawer.styles.scss';

type ManageListViewDrawerProps = {
  className?: string;
  initialHideValues?: string[];
  saveShownColumns?: boolean;
  updateProp: ManageTableColumns;
  options: Option[];
};

const ManageListViewDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  ManageListViewDrawerProps
> = (
  { className, updateProp, options, saveShownColumns, initialHideValues = [] },
  ref,
) => {
  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const { t } = useTranslation();
  const { popError } = useContext(PopNotificationsContext);
  const { patchCurrentUser } = useContext(CurrentUserContext);

  const classes = classNames('manage-list-view-drawer', className);

  const initialValues = useMemo(
    () => ({
      columns: Object.fromEntries([
        ...options.map((opt) => [opt.value, true]),
        ...initialHideValues
          .filter((col) => options.some((opt) => opt.value === col))
          .map((key) => [key, false]),
      ]),
    }),
    [initialHideValues, options],
  );

  const handleOnClose = useCallback(() => drawer?.setOpen(false), [drawer]);

  const handleOnSubmit = useCallback(
    async ({ columns }) => {
      try {
        const values = Object.entries(columns)
          .filter(([_, val]) => (saveShownColumns ? val : !val))
          .map(([key]) => key);

        await patchCurrentUser({ [updateProp]: values }, { skipToast: true });
        handleOnClose?.();
      } catch {
        popError(t('errorSelectedColumns'));
      }
    },
    [
      updateProp,
      saveShownColumns,
      patchCurrentUser,
      handleOnClose,
      popError,
      t,
    ],
  );

  return (
    <SideDrawer className={classes} ref={drawerRef}>
      <Form
        initialValues={initialValues}
        onSubmit={handleOnSubmit}
        render={({ handleSubmit, submitting, invalid, dirty }) => {
          return (
            <form onSubmit={handleSubmit}>
              <div className="manage-list-view-drawer__header">
                <h1>{t('manageListViewColumns')}</h1>
                <p>{t('selectColumnsToShowHide')}</p>
              </div>

              <CheckboxFieldGroup name="columns" required options={options} />

              <div className="manage-list-view-drawer__actions">
                <Button variant="link" onClick={handleOnClose}>
                  {t('cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={submitting || invalid || !dirty}
                >
                  {t('saveColumns')}
                </Button>
              </div>

              {submitting && <Loader />}
            </form>
          );
        }}
      />
    </SideDrawer>
  );
};

export default forwardRef(ManageListViewDrawer);
